import {
  ChartItemsType,
  ChartTableOfContentsType,
  DataFilterState,
  RootState,
  ScenarioType,
} from '@/types';
import { ActionTree, GetterTree, MutationTree } from 'vuex';
import {
  filterAreas,
  filterScenarios,
  filterSectors,
} from '@/assets/data/dataFilterOptions';
import { DataFilterActions } from '../actions/dataFilterActions';
import { chartData } from '@/assets/data/dataCharts';
import { DataFilterMutations } from '../mutations/dataFilterMutations';

const state: DataFilterState = {
  areas: filterAreas,
  scenarios: filterScenarios,
  sectors: filterSectors,
  selectedArea: null,
  selectedScenarios: filterScenarios,
  selectedSector: filterSectors[0],
  availableCharts: [],
  selectedChart: null,
  appliedFilters: {},
};

const getters: GetterTree<DataFilterState, RootState> = {
  areas: state => state.areas,
  scenarios: state => state.scenarios,
  sectors: state => state.sectors,
  selectedArea: state => state.selectedArea,
  selectedScenarios: state => state.selectedScenarios,
  selectedSector: state => state.selectedSector,
  selectedChart: state => state.selectedChart,
  availableCharts: state => state.availableCharts,
  selectedScenarioList: state => {
    return state.selectedScenarios.map(x => x.id);
  },
  appliedFilters: state => state.appliedFilters,
};

const mutations: MutationTree<DataFilterState> = {
  [DataFilterMutations.SET_SELECTED_AREA]: (state, newVal) => {
    state.selectedArea = newVal;
  },
  [DataFilterMutations.SET_SELECTED_SCENARIOS]: (state, newVal) => {
    // sort newVal array then set selected scenarios
    state.selectedScenarios = newVal.sort(
      (a: ScenarioType, b: ScenarioType) => {
        return a.order - b.order;
      },
    );
  },
  [DataFilterMutations.SET_SELECTED_SECTOR]: (state, newVal) => {
    state.selectedSector = newVal;
  },
  [DataFilterMutations.SET_AVAILABLE_CHARTS]: (state, newVal) => {
    state.availableCharts = newVal;
  },
  [DataFilterMutations.SET_SELECTED_CHART]: (state, newVal) => {
    state.selectedChart = newVal;
  },
  [DataFilterMutations.SET_APPLIED_FILTERS]: (state, newVal) => {
    state.appliedFilters = newVal;
  },
};

const actions: ActionTree<DataFilterState, RootState> = {
  [DataFilterActions.SET_DATA_FILTER]: ({ commit, state, dispatch }) => {
    let updateChartList = false;

    if (
      state.selectedArea !== state.appliedFilters.area ||
      state.selectedSector !== state.appliedFilters.sector
    ) {
      updateChartList = true;
    }

    commit(DataFilterMutations.SET_APPLIED_FILTERS, {
      area: state.selectedArea,
      sector: state.selectedSector,
      scenarios: state.selectedScenarios,
    });

    if (updateChartList) {
      dispatch(DataFilterActions.UPDATE_CHART_LIST);
    }
  },
  [DataFilterActions.UPDATE_CHART_LIST]: ({ commit, state }) => {
    const data: ChartTableOfContentsType[] = [];

    if (state.selectedSector && state.selectedArea) {
      chartData.forEach(category => {
        const group: { heading: string; items: ChartItemsType[] } = {
          heading: category.heading,
          items: [],
        };

        category.items.forEach(item => {
          if (
            // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
            item.sectors.includes(state.selectedSector!.id) &&
            // eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
            item.areas.includes(state.selectedArea!.id)
          ) {
            group.items.push(item as ChartItemsType);
          }
        });

        if (group.items.length > 0) {
          data.push(group);
        }

        commit(DataFilterMutations.SET_AVAILABLE_CHARTS, data);

        if (
          state.availableCharts.length > 0 &&
          state.availableCharts[0].items.length > 0
        ) {
          commit(
            DataFilterMutations.SET_SELECTED_CHART,
            state.availableCharts[0].items[0].id,
          );
        } else {
          commit(DataFilterMutations.SET_SELECTED_CHART, null);
        }
      });
    }
  },
  [DataFilterActions.SET_SELECTED_AREA_BY_BASIN_ID]: (
    { commit, state },
    payload,
  ) => {
    const area = state.areas.find(x => x.id === payload);
    if (area) {
      commit(DataFilterMutations.SET_SELECTED_AREA, area);
    }
  },
  [DataFilterActions.SET_SELECTED_AREA_BY_AREA_NAME]: (
    { commit, state },
    payload,
  ) => {
    const area = state.areas.find(area =>
      area.name
        .replace(/\s+/g, '')
        .toLowerCase()
        .includes(payload.replace(/\s+/g, '').toLowerCase()),
    );
    if (area) {
      commit(DataFilterMutations.SET_SELECTED_AREA, area);
    }
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};
